// TurnierAssistent.jsx — „Neues Turnier“ in fünf Schritten (18.10.2026)
//
//   1 Stammdaten   Name, Datum, Ort, Plakat (optional)
//   2 Preise       Startgebühr, Teamgebühr, Frühbucher, Nachmeldung, Schluss, IBAN
//   3 Divisionen   Vorlage: früheres Turnier des Verbands oder Katalog; Matten
//   4 Ausschreibung Zeitplan, Zahlung, Haftung, Hotels, PDF-Anhänge (22.10.2026,
//                  components/turnier/AusschreibungEditor, freiwillig)
//   5 Prüfen       Vorbereitungsstand, „Anmeldung öffnen“
//
// Nach jedem Schritt wird gespeichert. Das Turnier existiert ab Schritt 1 als
// Entwurf (Adresse /dashboard/turnier-neu/<id>?schritt=n), ein Abbruch
// verliert nichts. Bearbeitet wird danach in der vollen Maske
// (TurnierBearbeitenPage, Reiter). Der Server prüft alles selbst; hier steht
// nur, was er antwortet (jede Antwort mit res.ok geprüft).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ibanGueltig } from '../../lib/iban';
import { statusText, statusWechseln, TURNIER_STATUS } from '../../lib/turnierStatus';
import '../../styles/TurnierAblauf.css';
import AusschreibungEditor from '../../components/turnier/AusschreibungEditor';
import { TURNIER_ARTEN } from '../../lib/turnierArt';

const SCHRITTE = ['Stammdaten', 'Preise und Fristen', 'Divisionen', 'Ausschreibung', 'Prüfen'];
const PRUEFEN = SCHRITTE.length;
const ARTEN = ['Formen', 'Kumite', 'Pointfighting', 'Continous Fighting', 'Fullcontact', 'K1', 'Grappling', 'Rumble', 'Selbstverteidigung', 'Bruchtest'];
const ART_TEXT = { 'Continous Fighting': 'Continuous Fighting' };

const leer = {
  name: '', art: 'Turnier', start_datum: '', end_datum: '', ort: '', adresse: '', plz: '', stadt: '',
  preis_einzel: '', preis_team: '', teamgebuehr_modus: 'aufteilen', nachmeldegebuehr: '', anmeldeschluss: '',
  bank_kontoinhaber: '', bank_iban: '', bank_bic: '', bank_name: '',
};

const tag = (v) => (v ? String(v).slice(0, 10) : '');

async function antwort(res) {
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.success === false) {
    const det = Array.isArray(d.details) ? d.details.join(' · ') : '';
    throw Object.assign(new Error(det || d.error || d.message || `Fehler ${res.status}`), { daten: d });
  }
  return d;
}

export default function TurnierAssistent() {
  const navigate = useNavigate();
  const location = useLocation();
  const teile = location.pathname.split('/');
  const idAusPfad = teile[teile.indexOf('turnier-neu') + 1] || null;
  const turnierId = idAusPfad && /^\d+$/.test(idAusPfad) ? Number(idAusPfad) : null;
  const schrittAusUrl = Number(new URLSearchParams(location.search).get('schritt')) || 1;
  const [schritt, setSchritt] = useState(turnierId ? Math.min(Math.max(schrittAusUrl, 1), PRUEFEN) : 1);
  const ausschreibungRef = useRef(null);

  const token = localStorage.getItem('token');
  const kopf = useCallback((json = true) => ({
    Authorization: `Bearer ${token}`, ...(json ? { 'Content-Type': 'application/json' } : {}),
  }), [token]);

  const [form, setForm] = useState(leer);
  const [turnier, setTurnier] = useState(null);
  const [plakat, setPlakat] = useState(null);
  const [perioden, setPerioden] = useState([]);       // [{id?, bezeichnung, von_datum, bis_datum, rabatt_prozent}]
  const [periodenAlt, setPeriodenAlt] = useState([]); // ids beim Laden
  const [auswahl, setAuswahl] = useState([]);         // gewählte Divisionen
  const [vorlage, setVorlage] = useState('katalog');  // 'katalog' | 'turnier'
  const [arten, setArten] = useState([]);
  const [quelle, setQuelle] = useState('');
  const [turniere, setTurniere] = useState([]);
  const [matten, setMatten] = useState(0);
  const [mattenSoll, setMattenSoll] = useState('');
  const [stand, setStand] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [hinweis, setHinweis] = useState('');
  // Start (nur ohne Turnier): „leer“ oder „wie Turnier X“ (POST /duplicate)
  const vorlageAusUrl = new URLSearchParams(location.search).get('vorlage') || '';
  const [start, setStart] = useState(vorlageAusUrl ? 'vorlage' : 'leer');
  const [startQuelle, setStartQuelle] = useState(vorlageAusUrl);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const laden = useCallback(async () => {
    if (!turnierId) return;
    try {
      const t = (await antwort(await fetch(`/api/turniere/${turnierId}`, { headers: kopf() }))).data;
      setTurnier(t);
      setForm({
        ...leer,
        ...Object.fromEntries(Object.keys(leer).map((k) => [k, t[k] ?? ''])),
        start_datum: tag(t.start_datum || t.datum), end_datum: tag(t.end_datum), anmeldeschluss: tag(t.anmeldeschluss),
      });
      const p = await antwort(await fetch(`/api/turniere/${turnierId}/fruehbucher`, { headers: kopf() }));
      const rows = (p.data || []).map((r) => ({ ...r, von_datum: tag(r.von_datum), bis_datum: tag(r.bis_datum) }));
      setPerioden(rows); setPeriodenAlt(rows.map((r) => r.id));
      const dv = await antwort(await fetch(`/api/turniere/${turnierId}/divisionen`, { headers: kopf() }));
      setAuswahl(dv.data || []);
      const kf = await fetch(`/api/kampfflaechen/${turnierId}`, { headers: kopf() });
      if (kf.ok) {
        const k = await kf.json();
        const liste = Array.isArray(k) ? k : (k.data || []);
        // Höchste vorhandene Nummer, nicht die Anzahl: Nummern müssen eindeutig sein.
        const n = liste.reduce((m, f) => Math.max(m, Number(f.nummer) || 0), 0);
        setMatten(n); setMattenSoll((m) => m || String(n || 1));
      }
    } catch (e) { setFehler(e.message); }
  }, [turnierId, kopf]);

  useEffect(() => { laden(); }, [laden]);

  useEffect(() => {
    if ((schritt !== 3 && turnierId) || turniere.length) return;
    fetch('/api/turniere?status=all&limit=100', { headers: kopf() })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((d) => setTurniere((d.data || []).filter((t) => t.turnier_id !== turnierId)))
      .catch(() => {});
  }, [schritt, turniere.length, turnierId, kopf]);

  useEffect(() => {
    if (schritt !== PRUEFEN || !turnierId) return;
    fetch(`/api/turniere/${turnierId}/vorbereitungsstand`, { headers: kopf() })
      .then(antwort).then((d) => setStand(d.data || d)).catch((e) => setFehler(e.message));
  }, [schritt, turnierId, kopf]);

  // Vorlage gewählt: Namen mit dem nächsten Jahr vorschlagen, Datum bleibt
  // Pflichteingabe (kein stilles „+1 Jahr“ fürs Turnierdatum selbst).
  useEffect(() => {
    if (turnierId || start !== 'vorlage' || !startQuelle) return;
    const q = turniere.find((t) => String(t.turnier_id) === String(startQuelle));
    if (q && !form.name) set('name', String(q.name).replace(/\b(20\d\d)\b/, (j) => String(Number(j) + 1)));
  }, [startQuelle, turniere]); // eslint-disable-line react-hooks/exhaustive-deps -- nur beim Wählen der Vorlage

  const ausVorlage = async () => {
    if (!startQuelle) return setFehler('Bitte ein Turnier als Vorlage wählen.');
    if (form.name.trim().length < 3) return setFehler('Der Turniername braucht mindestens 3 Zeichen.');
    if (!form.start_datum) return setFehler('Bitte das neue Turnierdatum eintragen.');
    setBusy(true); setFehler('');
    try {
      const d = await antwort(await fetch(`/api/turniere/${startQuelle}/duplicate`, {
        method: 'POST', headers: kopf(), body: JSON.stringify({ name: form.name.trim(), datum: form.start_datum }),
      }));
      const k = d.kopiert || {};
      setSchritt(1);
      navigate(`/dashboard/turnier-neu/${d.data.turnier_id}?schritt=1`, { replace: true });
      setHinweis(`Übernommen: ${k.divisionen ?? 0} Divisionen, ${k.kampfflaechen ?? 0} Matten, ${k.fruehbucher ?? 0} Frühbucher-Zeiträume, ${k.personal_zuweisungen ?? 0} Personal-Einteilungen. Alle Daten um ${d.verschiebung_tage ?? 365} Tage verschoben. Nicht übernommen: ${(d.nicht_kopiert || []).join(', ')}. Bitte die Schritte durchsehen.`);
    } catch (e) { setFehler(e.message); } finally { setBusy(false); }
  };

  const zuSchritt = (n, id = turnierId) => {
    setFehler(''); setHinweis('');
    setSchritt(n);
    navigate(`/dashboard/turnier-neu/${id}?schritt=${n}`, { replace: true });
  };

  // PUT braucht Name, Datum und Ort jedes Mal (validateTurnier).
  const speichernFelder = async (felder) => {
    const body = { name: form.name, start_datum: form.start_datum, end_datum: form.end_datum || form.start_datum, ort: form.ort, ...felder };
    return antwort(await fetch(`/api/turniere/${turnierId}`, { method: 'PUT', headers: kopf(), body: JSON.stringify(body) }));
  };

  const schritt1 = async () => {
    if (form.name.trim().length < 3) return setFehler('Der Turniername braucht mindestens 3 Zeichen.');
    if (!form.start_datum) return setFehler('Bitte das Turnierdatum eintragen.');
    if (form.ort.trim().length < 2) return setFehler('Bitte die Halle bzw. den Ort eintragen.');
    if (form.end_datum && form.end_datum < form.start_datum) return setFehler('Das Enddatum liegt vor dem Startdatum.');
    setBusy(true); setFehler('');
    try {
      const felder = { art: form.art || 'Turnier', adresse: form.adresse, plz: form.plz, stadt: form.stadt };
      let id = turnierId;
      if (!id) {
        const d = await antwort(await fetch('/api/turniere', {
          method: 'POST', headers: kopf(),
          body: JSON.stringify({ name: form.name, start_datum: form.start_datum, end_datum: form.end_datum || form.start_datum, ort: form.ort, ...felder }),
        }));
        id = d.data.turnier_id;
      } else {
        await speichernFelder(felder);
      }
      if (plakat) {
        const fd = new FormData(); fd.append('file', plakat); fd.append('type', 'plakat');
        try { await antwort(await fetch(`/api/turniere/${id}/upload`, { method: 'POST', headers: kopf(false), body: fd })); setPlakat(null); }
        catch (e) { setHinweis(`Turnier gespeichert, Plakat nicht: ${e.message}. Es lässt sich später unter „Logo & Plakat“ hochladen.`); }
      }
      zuSchritt(2, id);
    } catch (e) { setFehler(e.message); } finally { setBusy(false); }
  };

  const schritt2 = async () => {
    if (form.bank_iban && !ibanGueltig(form.bank_iban)) return setFehler('Die IBAN ist nicht gültig (Prüfziffer).');
    if (form.anmeldeschluss && form.start_datum && form.anmeldeschluss > form.start_datum) return setFehler('Der Anmeldeschluss liegt nach dem Turniertag.');
    for (const p of perioden) {
      if (!p.von_datum || !p.bis_datum || !(Number(p.rabatt_prozent) > 0)) return setFehler('Jeder Frühbucher-Zeitraum braucht von, bis und einen Rabatt in Prozent.');
      if (p.bis_datum < p.von_datum) return setFehler('Ein Frühbucher-Zeitraum endet vor seinem Beginn.');
    }
    setBusy(true); setFehler('');
    try {
      await speichernFelder({
        preis_einzel: form.preis_einzel, preis_team: form.preis_team, teamgebuehr_modus: form.teamgebuehr_modus,
        nachmeldegebuehr: form.nachmeldegebuehr || 0, anmeldeschluss: form.anmeldeschluss || null,
        bank_kontoinhaber: form.bank_kontoinhaber, bank_iban: String(form.bank_iban || '').replace(/\s+/g, '').toUpperCase(),
        bank_bic: form.bank_bic, bank_name: form.bank_name,
      });
      const jetzt = new Set(perioden.filter((p) => p.id).map((p) => p.id));
      for (const alt of periodenAlt) {
        if (!jetzt.has(alt)) await antwort(await fetch(`/api/turniere/${turnierId}/fruehbucher/${alt}`, { method: 'DELETE', headers: kopf() }));
      }
      for (const p of perioden) {
        const body = JSON.stringify({ bezeichnung: p.bezeichnung || null, von_datum: p.von_datum, bis_datum: p.bis_datum, rabatt_prozent: Number(p.rabatt_prozent), sort_order: 0 });
        const url = p.id ? `/api/turniere/${turnierId}/fruehbucher/${p.id}` : `/api/turniere/${turnierId}/fruehbucher`;
        await antwort(await fetch(url, { method: p.id ? 'PUT' : 'POST', headers: kopf(), body }));
      }
      await laden();
      zuSchritt(3);
    } catch (e) { setFehler(e.message); } finally { setBusy(false); }
  };

  const vorlageUebernehmen = async () => {
    setBusy(true); setFehler(''); setHinweis('');
    try {
      if (vorlage === 'turnier') {
        if (!quelle) throw new Error('Bitte ein Turnier als Vorlage wählen.');
        const d = await antwort(await fetch(`/api/turniere/${turnierId}/divisionen/kopieren/${quelle}`, { method: 'POST', headers: kopf(), body: '{}' }));
        setHinweis(`${d.copied ?? 0} Divisionen übernommen.`);
      } else {
        if (!arten.length) throw new Error('Bitte mindestens eine Art wählen.');
        const d = await antwort(await fetch(`/api/turniere/${turnierId}/divisionen`, { method: 'POST', headers: kopf(), body: JSON.stringify({ katalog_typen: arten }) }));
        setHinweis(`${d.added ?? 0} Divisionen aus dem Katalog übernommen.`);
      }
      const dv = await antwort(await fetch(`/api/turniere/${turnierId}/divisionen`, { headers: kopf() }));
      setAuswahl(dv.data || []);
    } catch (e) { setFehler(e.message); } finally { setBusy(false); }
  };

  const schritt3 = async () => {
    const soll = Number(mattenSoll);
    if (!auswahl.length) return setFehler('Bitte zuerst Divisionen übernehmen (oder später im Reiter „Divisionen“).');
    if (!(soll >= 1 && soll <= 20)) return setFehler('Bitte die Anzahl der Matten eintragen (1 bis 20).');
    setBusy(true); setFehler('');
    try {
      // Nur fehlende Matten anlegen; vorhandene bleiben (auch wenn es mehr sind).
      for (let n = matten + 1; n <= soll; n++) {
        await antwort(await fetch(`/api/kampfflaechen/${turnierId}`, { method: 'POST', headers: kopf(), body: JSON.stringify({ name: `Matte ${n}`, nummer: n, groesse: null, standort: null }) /* die Route bindet beide ohne Rückfall */ }));
      }
      if (soll < matten) setHinweis(`Es gibt schon ${matten} Matten, es wurde keine entfernt. Weniger Matten unter „Kampfflächen“ abschalten.`);
      setMatten(Math.max(matten, soll));
      zuSchritt(4);
    } catch (e) { setFehler(e.message); } finally { setBusy(false); }
  };

  // Schritt 4: der Editor speichert selbst (eine PUT-Anfrage, Server prüft)
  const schritt4 = async () => {
    const ok = await ausschreibungRef.current?.speichern();
    if (ok) zuSchritt(PRUEFEN);
  };

  const oeffnen = async () => {
    setBusy(true); setFehler('');
    const erg = await statusWechseln(turnierId, TURNIER_STATUS.AKTIV);
    setBusy(false);
    if (!erg.ok) {
      setFehler(`${erg.error}${erg.fehlend?.length ? ` ${erg.fehlend.map((p) => `${p.titel}: ${p.text}`).join(' · ')}` : ''}`);
      return;
    }
    navigate(`/dashboard/turnier-bearbeiten/${turnierId}`);
  };

  const artenListe = [...new Set(auswahl.map((d) => d.Division_Type || 'Sonstige'))];

  return (
    <div className="tw-seite">
      <div className="tw-kopf">
        <div>
          <h1>{turnierId ? (form.name || 'Neues Turnier') : 'Neues Turnier'}</h1>
          <p className="ta-klein">In fünf Schritten zur offenen Anmeldung. Nach jedem Schritt ist gespeichert.
            {turnier && <> Status: <strong>{statusText(turnier.status_wirksam || turnier.status)}</strong></>}</p>
        </div>
        <div className="ta-knoepfe">
          <button type="button" className="ta-btn ta-btn-leise" onClick={() => navigate('/dashboard/turniere')}>Zur Turnierliste</button>
          {turnierId && <button type="button" className="ta-btn ta-btn-leise" onClick={() => navigate(`/dashboard/turnier-bearbeiten/${turnierId}`)}>Volle Maske</button>}
        </div>
      </div>

      <ol className="ta-schritte tw-schritte">
        {SCHRITTE.map((s, i) => (
          <li key={s} className={`ta-schritt ${i + 1 < schritt ? 'ta-erledigt' : ''} ${i + 1 === schritt ? 'ta-aktuell' : ''}`}>
            <button type="button" className="tw-schritt-knopf" disabled={!turnierId || busy} onClick={() => zuSchritt(i + 1)}>
              <span className="ta-punkt">{i + 1 < schritt ? '✓' : i + 1}</span>
              <span className="ta-name">{s}</span>
            </button>
          </li>
        ))}
      </ol>

      {fehler && <div className="ta-meldung ta-meldung-fehler" role="alert">{fehler}</div>}
      {hinweis && <div className="ta-meldung ta-meldung-ok" role="status">{hinweis}</div>}

      <div className="ta-leiste tw-karte">
        {schritt === 1 && !turnierId && (
          <div className="tw-wahl tw-start">
            <label><input type="radio" checked={start === 'leer'} onChange={() => setStart('leer')} /> Leer beginnen</label>
            <label><input type="radio" checked={start === 'vorlage'} onChange={() => setStart('vorlage')} /> Wie ein früheres Turnier (aus Vorjahr übernehmen)</label>
          </div>
        )}
        {schritt === 1 && !turnierId && start === 'vorlage' && (
          <div className="tw-raster">
            <label className="tw-feld tw-breit">Vorlage
              <select value={startQuelle} onChange={(e) => setStartQuelle(e.target.value)}>
                <option value="">Turnier wählen …</option>
                {turniere.map((t) => <option key={t.turnier_id} value={t.turnier_id}>{t.name}{t.datum ? ` (${tag(t.datum).split('-').reverse().join('.')})` : ''}</option>)}
              </select>
            </label>
            <label className="tw-feld">Neuer Name *
              <input value={form.name} onChange={(e) => set('name', e.target.value)} />
            </label>
            <label className="tw-feld">Neues Datum *
              <input type="date" value={form.start_datum} onChange={(e) => set('start_datum', e.target.value)} />
            </label>
            <p className="ta-klein tw-breit">Übernommen werden Ort, Preise, IBAN, Frühbucher, Fristen, Divisionen, Matten, Personal-Einteilung, Hinweistexte und Ranglisten-Faktor; alle Daten wandern mit dem neuen Turnierdatum. Nicht übernommen: Plakat, Anmeldungen, Rabattcodes und die Konten von Kampfrichtern, Waage und Registrierung. Das neue Turnier ist ein Entwurf.</p>
          </div>
        )}
        {schritt === 1 && (turnierId || start === 'leer') && (
          <div className="tw-raster">
            <label className="tw-feld tw-breit">Turniername *
              <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="z. B. Dragon Cup 2027" />
            </label>
            <label className="tw-feld tw-breit">Art des Turniers
              <select value={form.art || 'Turnier'} onChange={(e) => set('art', e.target.value)}>
                {TURNIER_ARTEN.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <label className="tw-feld">Datum *
              <input type="date" value={form.start_datum} onChange={(e) => set('start_datum', e.target.value)} />
            </label>
            <label className="tw-feld">Letzter Tag (bei mehreren Tagen)
              <input type="date" value={form.end_datum} onChange={(e) => set('end_datum', e.target.value)} />
            </label>
            <label className="tw-feld tw-breit">Halle / Ort *
              <input value={form.ort} onChange={(e) => set('ort', e.target.value)} placeholder="z. B. Sporthalle am Park" />
            </label>
            <label className="tw-feld">Straße
              <input value={form.adresse} onChange={(e) => set('adresse', e.target.value)} />
            </label>
            <label className="tw-feld">PLZ und Stadt
              <span className="tw-zwei">
                <input value={form.plz} onChange={(e) => set('plz', e.target.value)} placeholder="PLZ" />
                <input value={form.stadt} onChange={(e) => set('stadt', e.target.value)} placeholder="Stadt" />
              </span>
            </label>
            <label className="tw-feld tw-breit">Plakat (optional, Bild)
              <input type="file" accept="image/*" onChange={(e) => setPlakat(e.target.files?.[0] || null)} />
              {turnier?.plakat_url && <span className="ta-klein">Ein Plakat ist schon hochgeladen.</span>}
            </label>
          </div>
        )}

        {schritt === 2 && (
          <div className="tw-raster">
            <label className="tw-feld">Startgebühr je Start (€)
              <input type="number" min="0" step="0.5" value={form.preis_einzel} onChange={(e) => set('preis_einzel', e.target.value)} />
            </label>
            <label className="tw-feld">Teamgebühr (€)
              <input type="number" min="0" step="0.5" value={form.preis_team} onChange={(e) => set('preis_team', e.target.value)} />
            </label>
            <label className="tw-feld">Teamgebühr gilt
              <select value={form.teamgebuehr_modus} onChange={(e) => set('teamgebuehr_modus', e.target.value)}>
                <option value="aufteilen">Auf die Teammitglieder aufteilen</option>
                <option value="verein">Eine Position für den Verein</option>
              </select>
            </label>
            <label className="tw-feld">Nachmeldeaufschlag je Start (€)
              <input type="number" min="0" step="0.5" value={form.nachmeldegebuehr} onChange={(e) => set('nachmeldegebuehr', e.target.value)} />
            </label>
            <label className="tw-feld">Anmeldeschluss
              <input type="date" value={form.anmeldeschluss} onChange={(e) => set('anmeldeschluss', e.target.value)} />
              <span className="ta-klein">Leer: Vereine melden auch am Turniertag, bis die Kategorie gestartet ist.</span>
            </label>
            <div className="tw-feld tw-breit">
              <span>Frühbucher-Zeiträume</span>
              {perioden.map((p, i) => (
                <div key={p.id || `neu${i}`} className="tw-periode">
                  <input aria-label="Frühbucher Bezeichnung" placeholder="Bezeichnung" value={p.bezeichnung || ''} onChange={(e) => setPerioden((ps) => ps.map((x, j) => (j === i ? { ...x, bezeichnung: e.target.value } : x)))} />
                  <input type="date" aria-label="Frühbucher von" value={p.von_datum} onChange={(e) => setPerioden((ps) => ps.map((x, j) => (j === i ? { ...x, von_datum: e.target.value } : x)))} />
                  <input type="date" aria-label="Frühbucher bis" value={p.bis_datum} onChange={(e) => setPerioden((ps) => ps.map((x, j) => (j === i ? { ...x, bis_datum: e.target.value } : x)))} />
                  <input type="number" min="1" max="100" aria-label="Frühbucher Rabatt in Prozent" placeholder="%" value={p.rabatt_prozent ?? ''} onChange={(e) => setPerioden((ps) => ps.map((x, j) => (j === i ? { ...x, rabatt_prozent: e.target.value } : x)))} />
                  <button type="button" className="ta-btn ta-btn-leise" onClick={() => setPerioden((ps) => ps.filter((_, j) => j !== i))}>Entfernen</button>
                </div>
              ))}
              <button type="button" className="ta-btn ta-btn-leise" onClick={() => setPerioden((ps) => [...ps, { bezeichnung: '', von_datum: '', bis_datum: '', rabatt_prozent: '' }])}>Zeitraum hinzufügen</button>
            </div>
            <label className="tw-feld">Kontoinhaber
              <input value={form.bank_kontoinhaber} onChange={(e) => set('bank_kontoinhaber', e.target.value)} />
            </label>
            <label className="tw-feld">IBAN
              <input value={form.bank_iban} onChange={(e) => set('bank_iban', e.target.value)} placeholder="DE…" />
              {form.bank_iban && !ibanGueltig(form.bank_iban) && <span className="ta-klein">Prüfziffer stimmt nicht.</span>}
            </label>
            <label className="tw-feld">BIC
              <input value={form.bank_bic} onChange={(e) => set('bank_bic', e.target.value)} />
            </label>
            <label className="tw-feld">Bank
              <input value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} />
            </label>
          </div>
        )}

        {schritt === 3 && (
          <div>
            <div className="tw-wahl">
              <label><input type="radio" checked={vorlage === 'katalog'} onChange={() => setVorlage('katalog')} /> Aus dem Katalog nach Art</label>
              <label><input type="radio" checked={vorlage === 'turnier'} onChange={() => setVorlage('turnier')} /> Wie bei einem früheren Turnier</label>
            </div>
            {vorlage === 'katalog' ? (
              <div className="tw-arten">
                {ARTEN.map((a) => (
                  <label key={a} className="tw-art">
                    <input type="checkbox" checked={arten.includes(a)} onChange={() => setArten((l) => (l.includes(a) ? l.filter((x) => x !== a) : [...l, a]))} />
                    {ART_TEXT[a] || a}
                  </label>
                ))}
              </div>
            ) : (
              <label className="tw-feld">Vorlage
                <select value={quelle} onChange={(e) => setQuelle(e.target.value)}>
                  <option value="">Turnier wählen …</option>
                  {turniere.map((t) => <option key={t.turnier_id} value={t.turnier_id}>{t.name}{t.datum ? ` (${tag(t.datum).split('-').reverse().join('.')})` : ''}</option>)}
                </select>
              </label>
            )}
            <div className="ta-knoepfe tw-abstand">
              <button type="button" className="ta-btn" disabled={busy} onClick={vorlageUebernehmen}>Divisionen übernehmen</button>
            </div>
            <p className="tw-stand">
              Gewählt: <strong>{auswahl.length}</strong> Divisionen{artenListe.length ? ` (${artenListe.map((a) => ART_TEXT[a] || a).join(', ')})` : ''}.
              {' '}Einzelne Divisionen ändern geht später im Reiter „Divisionen“.
            </p>
            <label className="tw-feld tw-schmal">Anzahl Matten / Kampfflächen
              <input type="number" min="1" max="20" value={mattenSoll} onChange={(e) => setMattenSoll(e.target.value)} />
              <span className="ta-klein">Angelegt: {matten}</span>
            </label>
          </div>
        )}

        {schritt === 4 && (
          <AusschreibungEditor ref={ausschreibungRef} turnierId={turnierId} />
        )}

        {schritt === PRUEFEN && (
          <div>
            {!stand ? <p>Prüfe …</p> : (
              <ul className="tw-pruefliste">
                {stand.veroeffentlichen.filter((p) => p.id !== 'status').map((p) => (
                  <li key={p.id} className={`tw-p-${p.status}`}>
                    <span className="tw-p-zeichen">{p.status === 'ok' ? '✓' : p.status === 'warnung' ? '!' : '✗'}</span>
                    <span><strong>{p.titel}:</strong> {p.text}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="ta-klein">Mit „Anmeldung öffnen“ ist das Turnier öffentlich sichtbar und Vereine können melden. Fehlt etwas (✗), sagt der Server, was.</p>
          </div>
        )}

        <div className="ta-knoepfe tw-fuss">
          {schritt > 1 && <button type="button" className="ta-btn ta-btn-leise" disabled={busy} onClick={() => zuSchritt(schritt - 1)}>Zurück</button>}
          {schritt === 1 && !turnierId && start === 'vorlage'
            ? <button type="button" className="ta-btn ta-btn-haupt" disabled={busy} onClick={ausVorlage}>{busy ? 'Übernimmt …' : 'Als Entwurf übernehmen'}</button>
            : schritt === 1 && <button type="button" className="ta-btn ta-btn-haupt" disabled={busy} onClick={schritt1}>{busy ? 'Speichert …' : 'Speichern und weiter'}</button>}
          {schritt === 2 && <button type="button" className="ta-btn ta-btn-haupt" disabled={busy} onClick={schritt2}>{busy ? 'Speichert …' : 'Speichern und weiter'}</button>}
          {schritt === 3 && <button type="button" className="ta-btn ta-btn-haupt" disabled={busy} onClick={schritt3}>{busy ? 'Speichert …' : 'Speichern und weiter'}</button>}
          {schritt === 4 && <>
            <button type="button" className="ta-btn ta-btn-leise" disabled={busy} onClick={() => zuSchritt(PRUEFEN)}>Überspringen</button>
            <button type="button" className="ta-btn ta-btn-haupt" disabled={busy} onClick={schritt4}>Speichern und weiter</button>
          </>}
          {schritt === PRUEFEN && <>
            <button type="button" className="ta-btn ta-btn-leise" onClick={() => navigate(`/dashboard/turnier-bearbeiten/${turnierId}`)}>Als Entwurf lassen</button>
            <button type="button" className="ta-btn ta-btn-haupt" disabled={busy} onClick={oeffnen}>{busy ? 'Einen Moment …' : 'Anmeldung öffnen'}</button>
          </>}
        </div>
      </div>
    </div>
  );
}
